// What the apartment workspace shows, decided from the server's answer only (docs/product-ux 08):
// three separate dimensions (handover progress, defects, exceptions) and one next action. There is
// no signing process yet, so nothing is ever called "delivered": a fully checked handover is
// "summarized, not signed".
import type {
  ApartmentDefect,
  ApartmentWorkspace,
  HandoverSummary,
  HandoverType,
} from '@sivan/shared';

export const TYPE_LABELS: Readonly<Record<HandoverType, string>> = {
  initial: 'מסירה ראשונית',
  final: 'מסירה סופית',
};

/** The newest handover of each type (the server lists them newest first). */
export function latestOf(
  handovers: readonly HandoverSummary[],
  type: HandoverType,
): HandoverSummary | null {
  return handovers.find((h) => h.type === type) ?? null;
}

export type StageState = 'none' | 'opened' | 'in_progress' | 'checked';

export interface Stage {
  readonly type: HandoverType;
  readonly handover: HandoverSummary | null;
  readonly state: StageState;
  /** Checked share, 0–100. */
  readonly percent: number;
  readonly text: string;
}

export function stageOf(handover: HandoverSummary | null, type: HandoverType): Stage {
  if (handover === null) return { type, handover, state: 'none', percent: 0, text: 'טרם נפתחה' };
  const progress = handover.progress;
  if (progress === null || progress.total === 0)
    return { type, handover, state: 'opened', percent: 0, text: 'נפתחה · הבדיקה טרם החלה' };
  const percent = Math.round((progress.checked / progress.total) * 100);
  if (progress.checked >= progress.total)
    return {
      type,
      handover,
      state: 'checked',
      percent: 100,
      text: 'סוכמה (לא חתומה)',
    };
  return {
    type,
    handover,
    state: 'in_progress',
    percent,
    text: `${String(progress.checked)} מתוך ${String(progress.total)} נבדקו`,
  };
}

export type NextAction =
  | {
      readonly kind: 'open';
      readonly type: HandoverType;
      readonly handoverId: string;
      readonly title: string;
      readonly button: string;
    }
  | {
      readonly kind: 'create';
      readonly type: HandoverType;
      readonly title: string;
      readonly button: string;
    }
  | { readonly kind: 'none'; readonly title: string; readonly reason: string };

/**
 * The one next step for this apartment: continue the open handover, or open the next one. A
 * final handover follows a fully checked initial one; nothing follows a fully checked final one
 * until signing exists.
 */
export function nextAction(workspace: ApartmentWorkspace): NextAction {
  const { permissions } = workspace;
  if (workspace.handovers === null)
    return {
      kind: 'none',
      title: 'מסירות הדירה אינן זמינות לך',
      reason: 'צפייה במסירות דורשת הרשאת מסירות בפרויקט.',
    };
  const initial = stageOf(latestOf(workspace.handovers, 'initial'), 'initial');
  const final = stageOf(latestOf(workspace.handovers, 'final'), 'final');
  const proceed = (stage: Stage, title: string): NextAction =>
    stage.handover === null
      ? { kind: 'none', title, reason: '' }
      : {
          kind: 'open',
          type: stage.type,
          handoverId: stage.handover.id,
          title,
          button: permissions.handoverEdit
            ? `המשך ${TYPE_LABELS[stage.type]}`
            : `צפייה ב${TYPE_LABELS[stage.type]}`,
        };
  const start = (type: HandoverType, title: string): NextAction =>
    permissions.handoverCreate
      ? { kind: 'create', type, title, button: `פתיחת ${TYPE_LABELS[type]}` }
      : { kind: 'none', title, reason: 'פתיחת מסירה אינה זמינה בתפקיד שלך.' };

  if (final.state === 'checked')
    return {
      kind: 'none',
      title: 'המסירה הסופית סוכמה (לא חתומה)',
      reason: 'אין עדיין תהליך חתימה, ולכן הדירה אינה מסומנת "נמסרה".',
    };
  if (final.handover !== null) return proceed(final, 'המסירה הסופית בתהליך');
  if (initial.state === 'checked')
    return start('final', 'המסירה הראשונית סוכמה · הבאה בתור: סופית');
  if (initial.handover !== null) return proceed(initial, 'המסירה הראשונית בתהליך');
  return start('initial', 'טרם נפתחה מסירה לדירה');
}

/**
 * Open findings still needing work (one repaired at reinspection no longer counts), and the
 * initial findings the final handover has not reinspected yet.
 */
export function defectCounts(workspace: ApartmentWorkspace): {
  readonly open: number;
  readonly awaiting: number;
} {
  const defects: readonly ApartmentDefect[] = workspace.defects ?? [];
  const final = latestOf(workspace.handovers ?? [], 'final');
  return {
    open: defects.filter((d) => d.reinspection !== 'repaired').length,
    awaiting: final?.progress?.awaitingReinspection ?? 0,
  };
}

export const DEFECT_STATE: Readonly<
  Record<'open' | 'pending' | 'repaired' | 'still_defective', { label: string; tone: string }>
> = {
  open: { label: 'פתוח', tone: 'defect' },
  pending: { label: 'לבדיקה חוזרת', tone: 'pending' },
  repaired: { label: 'תוקן בבדיקה חוזרת', tone: 'done' },
  still_defective: { label: 'עדיין לא תוקן', tone: 'defect' },
};
